import React, { useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';
import { Icon, RotatingIcon, theme, useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  OrderPlaybackCta,
  OrderPlaybackFadeInView,
  OrderPlaybackFooter,
  OrderPlaybackGoBack,
  OrderPlaybackLineItems,
  OrderPlaybackLocation,
  OrderPlaybackOrderDetails,
  OrderPlaybackTime,
  OrderPlaybackTimer,
  OrderPlaybackTitle,
} from './components';
import { getTime, useDate } from './utils';
import { type BagOrderPlaybackProps } from './utils';

/**
 * Order playback component for displaying order details.
 */
export const BagOrderPlayback = (props: BagOrderPlaybackProps) => {
  const {
    products,
    wantedTime = new Date(),
    isWantedTimeAsap,
    orderChannel,
    locationName,
    orderPlaybackTitle,
    orderPlaybackCta,
    orderPlaybackFooterTitle,
    orderPlaybackFooterLoading,
    orderPlaybackFooterCta,
    orderPlaybackConfig,
    isLoading,
    isPostOrder,
    goBack,
    confirmOrder,
  } = props;

  const {
    locationFadeIn,
    timeFadeIn,
    orderDetailsFadeIn,
    lineItemsFadeIn,
    confirmationFadeIn,
    playbackHeaderFadeDuration,
    playbackBodyFadeDuration,
    playbackTimerDuration,
  } = orderPlaybackConfig;

  const date = useDate(wantedTime, isWantedTimeAsap);
  const time = getTime(wantedTime, orderChannel);
  const count = useMemo(() => {
    return products.reduce((acc, product) => acc + product.quantity, 0);
  }, [products]);

  const { minWidth, currentHeight } = useResponsive();
  const withSmallHeight = minWidth.isSM && currentHeight.isSM;

  const isIntentConfirmationEnabled = useFeatureFlag('CELS-4725-stripe-client-intent-confirmation-enabled'); // prettier-ignore
  const isOrderPlaybackRedesignEnabled = useFeatureFlag('CELS-4719-order-playback-redesign-enabled'); // prettier-ignore

  if (isOrderPlaybackRedesignEnabled) {
    const headerFadeIn = playbackHeaderFadeDuration;
    const bodyFadeIn = playbackHeaderFadeDuration + playbackBodyFadeDuration;
    const timerDuration = playbackTimerDuration;
    const skipAnimation = Boolean(isLoading && isIntentConfirmationEnabled);

    const footerTitle =
      isLoading || isPostOrder
        ? orderPlaybackFooterLoading
        : orderPlaybackFooterTitle;

    return (
      <SafeAreaView
        edges={SAFE_AREA_EDGES}
        style={[
          styles.container,
          withSmallHeight && styles.containerSmallHeight,
        ]}
      >
        <View style={styles.headerContent}>
          <View style={styles.titleContainer}>
            <OrderPlaybackTitle title={orderPlaybackTitle} />

            {isPostOrder ? (
              <Icon
                name="IconBadgeCheck"
                color={theme.colors.KALE}
                width={24}
                height={24}
              />
            ) : (
              <RotatingIcon
                duration={600}
                delay={400}
                color={theme.colors.KALE}
                name="IconOrderPlaybackHexagon"
                width={24}
                height={24}
              />
            )}
          </View>

          <OrderPlaybackFadeInView fadeIn={isLoading ? 0 : headerFadeIn}>
            <OrderPlaybackLocation
              orderChannel={orderChannel}
              locationName={locationName}
            />
          </OrderPlaybackFadeInView>

          <OrderPlaybackFadeInView fadeIn={isLoading ? 0 : headerFadeIn}>
            <OrderPlaybackTime
              orderChannel={orderChannel}
              date={date}
              time={time}
            />
          </OrderPlaybackFadeInView>

          <OrderPlaybackFadeInView fadeIn={isLoading ? 0 : headerFadeIn}>
            <OrderPlaybackOrderDetails count={count} />
          </OrderPlaybackFadeInView>
        </View>

        <OrderPlaybackFadeInView fadeIn={isLoading ? 0 : headerFadeIn}>
          <OrderPlaybackTimer
            delay={bodyFadeIn}
            duration={timerDuration}
            skipAnimation={skipAnimation}
          />
        </OrderPlaybackFadeInView>

        <View style={styles.bodyContent}>
          <OrderPlaybackFadeInView
            style={styles.lineItems}
            fadeIn={isLoading ? 0 : bodyFadeIn}
          >
            <OrderPlaybackLineItems
              fadeIn={isLoading ? 0 : bodyFadeIn}
              products={products}
            />
          </OrderPlaybackFadeInView>
        </View>

        <OrderPlaybackFooter
          isLoading={isLoading || isPostOrder}
          title={footerTitle}
          cta={orderPlaybackFooterCta}
          onPressCta={goBack}
        />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      edges={SAFE_AREA_EDGES}
      style={[
        styles.containerLegacy,
        withSmallHeight && styles.containerSmallHeight,
      ]}
    >
      <View style={styles.titleContainer}>
        <OrderPlaybackTitle title={orderPlaybackTitle} />

        <OrderPlaybackGoBack
          goBack={goBack}
          isDisabled={isLoading || isPostOrder}
        />
      </View>

      <OrderPlaybackFadeInView fadeIn={isLoading ? 0 : locationFadeIn}>
        <OrderPlaybackLocation
          orderChannel={orderChannel}
          locationName={locationName}
        />
      </OrderPlaybackFadeInView>

      <OrderPlaybackFadeInView fadeIn={isLoading ? 0 : timeFadeIn}>
        <OrderPlaybackTime
          orderChannel={orderChannel}
          date={date}
          time={time}
        />
      </OrderPlaybackFadeInView>

      <OrderPlaybackFadeInView fadeIn={isLoading ? 0 : orderDetailsFadeIn}>
        <OrderPlaybackOrderDetails count={count} />
      </OrderPlaybackFadeInView>

      <OrderPlaybackFadeInView
        style={styles.lineItems}
        fadeIn={isLoading ? 0 : lineItemsFadeIn}
      >
        <View style={styles.divider} />

        <OrderPlaybackLineItems
          fadeIn={isLoading ? 0 : lineItemsFadeIn}
          products={products}
        />
      </OrderPlaybackFadeInView>

      <OrderPlaybackCta
        orderPlaybackCta={orderPlaybackCta}
        fadeIn={isLoading ? 0 : lineItemsFadeIn}
        animationDuration={confirmationFadeIn}
        isLoading={isLoading}
        isPostOrder={isPostOrder}
        onPress={confirmOrder}
      />
    </SafeAreaView>
  );
};

// ─── Constants ────────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: Edges = Platform.select({
  android: ['top'],
  default: [],
});

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    paddingTop: theme.spacing['10'],
  },
  containerLegacy: {
    flexGrow: 1,
    paddingTop: theme.spacing['10'],
    paddingBottom: theme.spacing['6'],
    paddingHorizontal: theme.spacing['4'],
    gap: theme.spacing['4'],
  },
  headerContent: {
    gap: theme.spacing['4'],
    paddingHorizontal: theme.spacing['4'],
    marginBottom: theme.spacing['4'],
  },
  bodyContent: {
    flexGrow: 1,
    gap: theme.spacing['4'],
    paddingHorizontal: theme.spacing['4'],
    marginTop: theme.spacing['4'],
  },
  containerSmallHeight: {
    paddingTop: theme.spacing['4'],
    paddingBottom: theme.spacing['4'],
  },
  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  lineItems: {
    flex: 1,
    gap: theme.spacing['4'],
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
    marginHorizontal: theme.spacing['4'],
  },
});
